import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface HistoricalTradition {
  era: string;
  material: string;
  style: string;
  color: string;
}

interface MaterialType {
  name: string;
  technique: string;
  texture: string;
  color: string;
}

interface FamousWork {
  name: string;
  period: string;
  color: string;
}

interface MosaicTemplateProps {
  topic: string;
  subtitle: string;
  traditions: HistoricalTradition[];
  materials: MaterialType[];
  tessellationShapes: string[];
  processSteps: string[];
  famousWorks: FamousWork[];
  evolutionStages: string[];
  stats: { value: string; label: string }[];
  bottomQuote: string;
}

const DEFAULT_PROPS: MosaicTemplateProps = {
  topic: '马赛克艺术与镶嵌工艺',
  subtitle: 'Mosaic Art & Tessellation Craft',
  traditions: [
    { era: '古罗马', material: '大理石与石灰石', style: '叙事性地板镶嵌画', color: '#fbbf24' },
    { era: '拜占庭', material: '金箔玻璃马赛克', style: '宗教圣像穹顶装饰', color: '#3b82f6' },
    { era: '伊斯兰', material: '釉面瓷砖(Zellige)', style: '几何与阿拉伯花纹', color: '#22c55e' },
    { era: '现代高迪', material: '碎瓷片(Trencadís)', style: '自由曲面有机拼贴', color: '#dc2626' },
  ],
  materials: [
    { name: '石材', technique: '凿切成规则小块', texture: '天然纹理，耐久千年', color: '#fbbf24' },
    { name: '玻璃', technique: '熔融后切割/压制', texture: '半透明折射光泽', color: '#3b82f6' },
    { name: '陶瓷', technique: '施釉烧制后切割', texture: '色彩鲜艳，耐候性强', color: '#dc2626' },
    { name: '贝壳', technique: '打磨切片镶嵌', texture: '珠光虹彩自然之美', color: '#22c55e' },
  ],
  tessellationShapes: ['三角形', '正方形', '六边形'],
  processSteps: ['设计图案', '切割材料', '铺底胶', '镶嵌拼贴', '勾缝打磨'],
  famousWorks: [
    { name: '庞贝遗址马赛克', period: '公元前1世纪', color: '#fbbf24' },
    { name: '圣索菲亚大教堂', period: '公元6世纪', color: '#3b82f6' },
    { name: '高迪奎尔公园', period: '20世纪初', color: '#dc2626' },
  ],
  evolutionStages: ['马赛克', '点彩画', '像素艺术'],
  stats: [
    { value: '4000+年', label: '马赛克艺术历史' },
    { value: '5000m²', label: '最大马赛克面积' },
    { value: 'Trencadís', label: '高迪碎瓷片技法' },
  ],
  bottomQuote: '每一块碎片都微不足道，但当它们拼合在一起，便成就了不朽的艺术',
};

const TILE_PALETTE = ['#fbbf24', '#3b82f6', '#dc2626', '#22c55e', '#6b7280', '#8b5cf6', '#f59e0b', '#06b6d4'];

const SECTION_TITLE: React.CSSProperties = {
  fontSize: 26,
  color: '#6b7280',
  marginBottom: 10,
  letterSpacing: '0.1em',
  fontWeight: 600,
};

export const MosaicTemplate: React.FC<Partial<MosaicTemplateProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerIn = spring({ frame, fps, config: { damping: 18, stiffness: 80 } });

  const tradStart = 50;
  const matStart = 60;
  const tessStart = 70;
  const processStart = 180;
  const worksStart = 200;
  const colorStart = 220;
  const evoStart = 300;
  const statsStart = 80;
  const quoteStart = 400;

  const quoteOpacity = interpolate(frame, [quoteStart, quoteStart + 30], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const tileRevealProgress = interpolate(frame, [20, 160], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const TILE_ROWS = 8;
  const TILE_COLS = 8;
  const TILE_SIZE = 30;
  const TILE_GAP = 3;
  const svgSize = TILE_ROWS * (TILE_SIZE + TILE_GAP);

  const tiles: { row: number; col: number; dist: number }[] = [];
  for (let r = 0; r < TILE_ROWS; r++) {
    for (let c = 0; c < TILE_COLS; c++) {
      const dist = Math.abs(r - (TILE_ROWS - 1) / 2) + Math.abs(c - (TILE_COLS - 1) / 2);
      tiles.push({ row: r, col: c, dist });
    }
  }
  const maxDist = Math.max(...tiles.map((t) => t.dist));

  const LEFT_W = 390;
  const CL = 440;
  const CT = 130;
  const CONTENT_W = 1920 - CL - 40;
  const BOTTOM_Y = 430;

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(135deg, #0f0a14 0%, #0a0610 60%, #110818 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: 'white',
        overflow: 'hidden',
        width: 1920,
        height: 1080,
      }}
    >
      {/* Background vertical lines */}
      {Array.from({ length: 16 }).map((_, i) => (
        <div
          key={`bgline-${i}`}
          style={{
            position: 'absolute',
            left: 60 + i * 120,
            top: 0,
            width: 1,
            height: 1080,
            background: '#fbbf24',
            opacity: 0.02,
          }}
        />
      ))}

      {/* ===== Left Panel: Mosaic SVG ===== */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: LEFT_W,
          height: 1080,
          background: 'linear-gradient(180deg, #0d0812 0%, #08050d 100%)',
        }}
      >
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            width: 60,
            height: 1080,
            background: 'linear-gradient(90deg, transparent, #0a061080)',
          }}
        />

        <svg
          style={{
            position: 'absolute',
            left: (LEFT_W - svgSize) / 2,
            top: (1080 - svgSize) / 2 + 60,
          }}
          width={svgSize}
          height={svgSize}
          viewBox={`0 0 ${svgSize} ${svgSize}`}
        >
          <defs>
            <filter id="mosaicGlow">
              <feGaussianBlur stdDeviation="2" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          {tiles.map(({ row, col, dist }, i) => {
            const normalizedDist = dist / maxDist;
            const tileThreshold = normalizedDist;
            const tileOpacity = interpolate(
              tileRevealProgress,
              [tileThreshold * 0.8, tileThreshold * 0.8 + 0.15],
              [0, 1],
              { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
            );
            const tileScale = interpolate(
              tileRevealProgress,
              [tileThreshold * 0.8, tileThreshold * 0.8 + 0.15],
              [0.3, 1],
              { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
            );
            const color = TILE_PALETTE[(row * TILE_COLS + col + row) % TILE_PALETTE.length];
            const x = col * (TILE_SIZE + TILE_GAP);
            const y = row * (TILE_SIZE + TILE_GAP);
            const cx = x + TILE_SIZE / 2;
            const cy = y + TILE_SIZE / 2;
            const isStarTile = (row + col) % 3 === 0;
            return (
              <g
                key={i}
                opacity={tileOpacity}
                transform={`translate(${cx},${cy}) scale(${tileScale}) translate(${-cx},${-cy})`}
              >
                {isStarTile ? (
                  <polygon
                    points={`${cx},${y + 2} ${cx + TILE_SIZE * 0.35},${cy - TILE_SIZE * 0.1} ${x + TILE_SIZE - 2},${cy} ${cx + TILE_SIZE * 0.35},${cy + TILE_SIZE * 0.1} ${cx},${y + TILE_SIZE - 2} ${cx - TILE_SIZE * 0.35},${cy + TILE_SIZE * 0.1} ${x + 2},${cy} ${cx - TILE_SIZE * 0.35},${cy - TILE_SIZE * 0.1}`}
                    fill={color}
                    opacity="0.85"
                  />
                ) : (
                  <rect x={x} y={y} width={TILE_SIZE} height={TILE_SIZE} rx="2" fill={color} opacity="0.85" />
                )}
              </g>
            );
          })}
          {Array.from({ length: TILE_ROWS + 1 }).map((_, i) => (
            <line
              key={`hline-${i}`}
              x1={0}
              y1={i * (TILE_SIZE + TILE_GAP) - TILE_GAP / 2}
              x2={svgSize}
              y2={i * (TILE_SIZE + TILE_GAP) - TILE_GAP / 2}
              stroke="#6b728020"
              strokeWidth={TILE_GAP}
            />
          ))}
          {Array.from({ length: TILE_COLS + 1 }).map((_, i) => (
            <line
              key={`vline-${i}`}
              x1={i * (TILE_SIZE + TILE_GAP) - TILE_GAP / 2}
              y1={0}
              x2={i * (TILE_SIZE + TILE_GAP) - TILE_GAP / 2}
              y2={svgSize}
              stroke="#6b728020"
              strokeWidth={TILE_GAP}
            />
          ))}
        </svg>

        <div
          style={{
            position: 'absolute',
            top: (1080 + svgSize) / 2 + 72,
            width: LEFT_W,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            fontSize: 20,
            color: '#6b7280',
            whiteSpace: 'nowrap',
            opacity: interpolate(frame, [80, 110], [0, 0.7], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            }),
          }}
        >
          <span>▲ 几何镶嵌图案 · 中心向外逐块显现</span>
          <span style={{ color: '#fbbf24', fontWeight: 700 }}>历史传承</span>
        </div>
      </div>

      {/* ===== Header ===== */}
      <div
        style={{
          position: 'absolute',
          top: 26,
          left: 0,
          width: 1920,
          paddingLeft: 40,
          paddingRight: 40,
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * 15}px)`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div
            style={{
              display: 'inline-block',
              padding: '4px 14px',
              background: '#3b1f0b',
              borderRadius: 6,
              fontSize: 20,
              fontWeight: 700,
              color: '#fbbf24',
              border: '1px solid #fbbf2430',
              flexShrink: 0,
            }}
          >
            🧩 镶嵌艺术
          </div>
          <h1 style={{ fontSize: 34, fontWeight: 900, margin: 0, lineHeight: 1.2, flexShrink: 0 }}>
            {p.topic}
          </h1>
          <p style={{ fontSize: 24, color: '#6b7280', margin: 0, flexShrink: 0 }}>{p.subtitle}</p>
        </div>
        <div style={{ display: 'flex', gap: 14, flexShrink: 0 }}>
          {p.stats.map((st, i) => {
            const stIn = spring({
              frame: frame - (statsStart + i * 10),
              fps,
              config: { damping: 14, stiffness: 100 },
            });
            return (
              <div
                key={i}
                style={{
                  background: '#110c18',
                  borderRadius: 10,
                  padding: '8px 16px',
                  textAlign: 'center',
                  border: '1px solid #fbbf2420',
                  opacity: stIn,
                  transform: `scale(${0.85 + 0.15 * stIn})`,
                }}
              >
                <div style={{ fontSize: 22, fontWeight: 900, color: '#fbbf24' }}>{st.value}</div>
                <div style={{ fontSize: 20, color: '#6b7280', marginTop: 2 }}>{st.label}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Separator */}
      <div
        style={{
          position: 'absolute',
          top: 104,
          left: 0,
          width: 1920,
          height: 2,
          background: 'linear-gradient(90deg, #fbbf2430, #fbbf2408)',
        }}
      />

      {/* ===== Top: Tessellation + Process + Evolution ===== */}
      <div
        style={{
          position: 'absolute',
          top: CT,
          left: 0,
          width: 1920,
          paddingLeft: 40,
          paddingRight: 40,
          boxSizing: 'border-box',
          display: 'flex',
          gap: 20,
        }}
      >
        {/* 正则密铺数学 */}
        <div style={{ flex: 1 }}>
          <div style={SECTION_TITLE}>正则密铺数学</div>
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center' }}>
            {p.tessellationShapes.map((shape, i) => {
              const s = spring({
                frame: frame - (tessStart + i * 18),
                fps,
                config: { damping: 14, stiffness: 120 },
              });
              const tessColors = ['#fbbf24', '#3b82f6', '#22c55e'];
              return (
                <div
                  key={i}
                  style={{
                    textAlign: 'center',
                    opacity: s,
                    transform: `scale(${0.8 + 0.2 * s})`,
                  }}
                >
                  <svg width="180" height="155" viewBox="0 0 140 120">
                    {i === 0 &&
                      Array.from({ length: 12 }).map((_, ti) => {
                        const row = Math.floor(ti / 4);
                        const col = ti % 4;
                        const flip = (row + col) % 2 === 0;
                        const bx = col * 35;
                        const by = row * 40 + 5;
                        return (
                          <polygon
                            key={ti}
                            points={
                              flip
                                ? `${bx + 17},${by} ${bx + 35},${by + 38} ${bx},${by + 38}`
                                : `${bx},${by} ${bx + 35},${by} ${bx + 17},${by + 38}`
                            }
                            fill="none"
                            stroke={tessColors[0]}
                            strokeWidth="1.5"
                            opacity="0.7"
                          />
                        );
                      })}
                    {i === 1 &&
                      Array.from({ length: 12 }).map((_, si) => {
                        const row = Math.floor(si / 4);
                        const col = si % 4;
                        return (
                          <rect
                            key={si}
                            x={col * 35}
                            y={row * 38 + 4}
                            width="33"
                            height="36"
                            rx="1"
                            fill="none"
                            stroke={tessColors[1]}
                            strokeWidth="1.5"
                            opacity="0.7"
                          />
                        );
                      })}
                    {i === 2 &&
                      Array.from({ length: 7 }).map((_, hi) => {
                        const positions = [
                          [70, 25], [30, 50], [110, 50],
                          [50, 80], [90, 80], [70, 55], [70, 95],
                        ];
                        const [hx, hy] = positions[hi];
                        const r = 22;
                        const pts = Array.from({ length: 6 })
                          .map((__, a) => {
                            const angle = (Math.PI / 3) * a - Math.PI / 6;
                            return `${hx + r * Math.cos(angle)},${hy + r * Math.sin(angle)}`;
                          })
                          .join(' ');
                        return (
                          <polygon
                            key={hi}
                            points={pts}
                            fill="none"
                            stroke={tessColors[2]}
                            strokeWidth="1.5"
                            opacity="0.7"
                          />
                        );
                      })}
                  </svg>
                  <div style={{ fontSize: 22, fontWeight: 700, color: tessColors[i], marginTop: 2 }}>{shape}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* 制作工艺流程 */}
        <div style={{ flex: 1 }}>
          <div style={SECTION_TITLE}>制作工艺流程</div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0 }}>
            {p.processSteps.map((step, i) => {
              const s = spring({
                frame: frame - (processStart + i * 16),
                fps,
                config: { damping: 14, stiffness: 120 },
              });
              const isLast = i === p.processSteps.length - 1;
              return (
                <React.Fragment key={i}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      opacity: s,
                      transform: `scale(${s})`,
                    }}
                  >
                    <div
                      style={{
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        background: '#1a1028',
                        border: '2.5px solid #fbbf24',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 24,
                        fontWeight: 800,
                        color: '#fbbf24',
                      }}
                    >
                      {i + 1}
                    </div>
                    <div
                      style={{
                        fontSize: 18,
                        fontWeight: 700,
                        color: '#e2e8f0',
                        marginTop: 5,
                        textAlign: 'center',
                        width: 76,
                      }}
                    >
                      {step}
                    </div>
                  </div>
                  {!isLast && (
                    <div
                      style={{
                        width: 26,
                        height: 2,
                        background: 'linear-gradient(90deg, #fbbf24, #fbbf2440)',
                        margin: '0 3px',
                        marginBottom: 22,
                        opacity: interpolate(
                          frame,
                          [processStart + i * 16 + 8, processStart + i * 16 + 20],
                          [0, 0.7],
                          { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
                        ),
                      }}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* 视觉演化 */}
        <div style={{ flex: 1 }}>
          <div style={SECTION_TITLE}>视觉演化</div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0 }}>
            {p.evolutionStages.map((stage, i) => {
              const s = spring({
                frame: frame - (evoStart + i * 20),
                fps,
                config: { damping: 14, stiffness: 120 },
              });
              const evoColors = ['#fbbf24', '#8b5cf6', '#06b6d4'];
              const isLast = i === p.evolutionStages.length - 1;
              return (
                <React.Fragment key={i}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      opacity: s,
                      transform: `scale(${0.8 + 0.2 * s})`,
                    }}
                  >
                    <svg width="104" height="78" viewBox="0 0 80 60">
                      {i === 0 &&
                        Array.from({ length: 9 }).map((_, ti) => (
                          <rect
                            key={ti}
                            x={(ti % 3) * 26 + 2}
                            y={Math.floor(ti / 3) * 20}
                            width="24"
                            height="18"
                            rx="1"
                            fill={TILE_PALETTE[ti % TILE_PALETTE.length]}
                            opacity="0.8"
                          />
                        ))}
                      {i === 1 &&
                        Array.from({ length: 20 }).map((_, di) => (
                          <circle
                            key={di}
                            cx={(di % 5) * 16 + 8}
                            cy={Math.floor(di / 5) * 15 + 8}
                            r="5"
                            fill={TILE_PALETTE[di % TILE_PALETTE.length]}
                            opacity="0.7"
                          />
                        ))}
                      {i === 2 &&
                        Array.from({ length: 24 }).map((_, pi) => (
                          <rect
                            key={pi}
                            x={(pi % 6) * 13}
                            y={Math.floor(pi / 6) * 15}
                            width="12"
                            height="14"
                            fill={TILE_PALETTE[pi % TILE_PALETTE.length]}
                            opacity="0.8"
                          />
                        ))}
                    </svg>
                    <div style={{ fontSize: 22, fontWeight: 700, color: evoColors[i], marginTop: 3 }}>{stage}</div>
                  </div>
                  {!isLast && (
                    <div
                      style={{
                        fontSize: 30,
                        color: '#334155',
                        margin: '0 18px',
                        marginBottom: 18,
                        opacity: interpolate(
                          frame,
                          [evoStart + i * 20 + 10, evoStart + i * 20 + 20],
                          [0, 0.7],
                          { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
                        ),
                      }}
                    >
                      →
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== Bottom: 4 modules in one row, vertical layout ===== */}
      <div
        style={{
          position: 'absolute',
          top: BOTTOM_Y,
          left: CL,
          width: CONTENT_W,
          display: 'flex',
          gap: 16,
        }}
      >
        {/* 历史传承 */}
        <div style={{ flex: 1 }}>
          <div style={SECTION_TITLE}>历史传承</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {p.traditions.map((t, i) => {
              const s = spring({
                frame: frame - (tradStart + i * 14),
                fps,
                config: { damping: 14, stiffness: 120 },
              });
              return (
                <div
                  key={i}
                  style={{
                    background: '#110c18',
                    borderRadius: 10,
                    padding: '10px 14px',
                    border: `1px solid ${t.color}30`,
                    opacity: s,
                    transform: `translateY(${(1 - s) * 15}px)`,
                  }}
                >
                  <div style={{ fontSize: 22, fontWeight: 800, color: t.color }}>{t.era}</div>
                  <div style={{ fontSize: 20, color: '#94a3b8', marginTop: 2 }}>材料：{t.material}</div>
                  <div style={{ fontSize: 20, color: '#6b7280', marginTop: 1 }}>风格：{t.style}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* 材料类型 */}
        <div style={{ flex: 1 }}>
          <div style={SECTION_TITLE}>材料类型</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {p.materials.map((m, i) => {
              const s = spring({
                frame: frame - (matStart + i * 14),
                fps,
                config: { damping: 14, stiffness: 120 },
              });
              return (
                <div
                  key={i}
                  style={{
                    background: '#110c18',
                    borderRadius: 10,
                    padding: '10px 14px',
                    border: `1px solid ${m.color}30`,
                    opacity: s,
                    transform: `scale(${0.85 + 0.15 * s})`,
                  }}
                >
                  <div style={{ fontSize: 22, fontWeight: 800, color: m.color }}>{m.name}</div>
                  <div style={{ fontSize: 20, color: '#94a3b8', marginTop: 2 }}>工艺：{m.technique}</div>
                  <div style={{ fontSize: 20, color: '#6b7280', marginTop: 1 }}>质感：{m.texture}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* 世界著名马赛克 */}
        <div style={{ flex: 1 }}>
          <div style={SECTION_TITLE}>世界著名马赛克</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {p.famousWorks.map((w, i) => {
              const s = spring({
                frame: frame - (worksStart + i * 16),
                fps,
                config: { damping: 14, stiffness: 110 },
              });
              return (
                <div
                  key={i}
                  style={{
                    background: '#110c18',
                    borderRadius: 10,
                    padding: '12px 14px',
                    borderLeft: `3px solid ${w.color}`,
                    opacity: s,
                    transform: `translateY(${(1 - s) * 15}px)`,
                  }}
                >
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#f8fafc' }}>{w.name}</div>
                  <div style={{ fontSize: 20, color: w.color, marginTop: 4, fontWeight: 600 }}>{w.period}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* 马赛克色彩理论 */}
        <div style={{ flex: 1 }}>
          <div style={SECTION_TITLE}>马赛克色彩理论</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[
              { label: '互补色', pairs: [['#dc2626', '#22c55e'], ['#3b82f6', '#fbbf24']], desc: '对比强烈·视觉冲击' },
              { label: '类似色', pairs: [['#3b82f6', '#06b6d4'], ['#fbbf24', '#f59e0b']], desc: '和谐过渡·统一感' },
            ].map((scheme, i) => {
              const s = spring({
                frame: frame - (colorStart + i * 18),
                fps,
                config: { damping: 14, stiffness: 120 },
              });
              return (
                <div
                  key={i}
                  style={{
                    background: '#110c18',
                    borderRadius: 10,
                    padding: '12px 14px',
                    border: '1px solid #1e293b',
                    opacity: s,
                    transform: `scale(${0.85 + 0.15 * s})`,
                  }}
                >
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#e2e8f0', marginBottom: 6 }}>{scheme.label}</div>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
                    {scheme.pairs.map((pair, pi) => (
                      <div key={pi} style={{ display: 'flex', gap: 3 }}>
                        {pair.map((c, ci) => (
                          <div key={ci} style={{ width: 22, height: 22, borderRadius: 4, background: c }} />
                        ))}
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: 20, color: '#6b7280' }}>{scheme.desc}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== Bottom Quote ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 22,
          left: 40,
          right: 40,
          background: '#14081a',
          borderRadius: 12,
          padding: '12px 20px',
          borderTop: '3px solid #fbbf24',
          opacity: quoteOpacity,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 24 }}>🧩</span>
          <span style={{ fontSize: 18, color: '#e2e8f0', lineHeight: 1.5 }}>{p.bottomQuote}</span>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 540], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 3,
          background: 'linear-gradient(90deg, #fbbf24, #3b82f6, #dc2626, #22c55e)',
        }}
      />
    </AbsoluteFill>
  );
};
